/**
 * Owner-side editor for the site-wide TRIBUTE / IN MEMORIAM page.
 *
 * Lives inside the OwnerBar's SITE MESSAGES dropdown. Publishes a single
 * evolving tribute doc (server-side `settings._id="tribute"`) that any
 * viewer can open via the auto-banner or the top-strip pill. URL-only
 * asset flow (hero image, audio, gallery photos) — matches the safest
 * codepath in the current storage environment (object storage PUTs
 * intermittently 500 in production; linking to any hosted URL bypasses
 * that entirely and still gets image-proxied on the render side).
 *
 * Owner UX guarantees:
 *  • APPLY saves + bumps `published_at`, which re-triggers the 24h
 *    banner for every viewer (dismissed-by-old-hash gets wiped).
 *  • CLEAR wipes the doc — disables the tribute and hides all viewer
 *    surfaces (banner + view) until the owner publishes again.
 *  • Every text field is capped server-side; the panel mirrors the
 *    caps in the input `maxLength` so the operator never gets a 400
 *    from a typo.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Heart, Plus, Trash2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const CAP_NAME = 120;
const CAP_OBIT = 4000;
const CAP_CAPTION = 200;
const CAP_URL = 1024;

const emptyDraft = {
  enabled: false,
  name: "",
  birth_year: "",
  death_year: "",
  obituary: "",
  hero_url: "",
  linked_channel_id: "",
  linked_video_id: "",
};

export default function TributePanel({ isOwner, onClose, authHeaders, channels }) {
  const [tribute, setTribute] = useState(null); // server doc (source of truth for gallery ids)
  const [draft, setDraft] = useState(emptyDraft);
  const [gallery, setGallery] = useState([]);
  const [galleryDraft, setGalleryDraft] = useState({ url: "", caption: "" });
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(""); // small confirmation line
  // Archive management — count + expandable list with per-row delete.
  // Kept collapsed by default so the panel stays scannable on first
  // open; the count chip in the header hints there's something to
  // explore.
  const [archives, setArchives] = useState([]);
  const [archivesOpen, setArchivesOpen] = useState(false);
  // Hidden <input type=file> for the audio-upload button below. Owned by
  // this component so the button + REPLACE / TEST / CLEAR chain share
  // one ref and can trigger the OS file picker without a visible input.
  const audioFileInputRef = useRef(null);

  const refetchArchives = async () => {
    try {
      const r = await axios.get(`${API}/site/tribute/archive`);
      setArchives(r.data?.items || []);
    } catch (e) { /* noop */ }
  };

  // Fetch on mount so the panel opens with the current published state.
  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/tribute`);
        setTribute(r.data || null);
        if (r.data) {
          setDraft({
            enabled: !!r.data.enabled,
            name: r.data.name || "",
            birth_year: r.data.birth_year ? String(r.data.birth_year) : "",
            death_year: r.data.death_year ? String(r.data.death_year) : "",
            obituary: r.data.obituary || "",
            hero_url: r.data.hero_url || "",
            linked_channel_id: r.data.linked_channel_id || "",
            linked_video_id: r.data.linked_video_id || "",
          });
          setGallery(Array.isArray(r.data.gallery) ? r.data.gallery : []);
        }
      } catch (e) { /* noop */ }
    })();
    refetchArchives();
  }, [isOwner]);

  const flashOK = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 2200);
  };

  const handleSave = async () => {
    if (draft.enabled && !draft.name.trim()) {
      setFlash("Add a name before publishing.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        enabled: !!draft.enabled,
        name: draft.name.trim().slice(0, CAP_NAME),
        birth_year: parseYear(draft.birth_year),
        death_year: parseYear(draft.death_year),
        obituary: draft.obituary.slice(0, CAP_OBIT),
        hero_url: draft.hero_url.trim().slice(0, CAP_URL) || null,
        linked_channel_id: draft.linked_channel_id || null,
        linked_video_id: draft.linked_video_id || null,
      };
      const r = await axios.post(`${API}/site/tribute`, payload, {
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });
      setTribute(r.data);
      setGallery(r.data.gallery || []);
      // Publish-over auto-archives the previous doc — refetch so the
      // count chip + list reflect it immediately.
      await refetchArchives();
      window.dispatchEvent(new CustomEvent("crt:tribute-updated", { detail: r.data }));
      flashOK(draft.enabled ? "Published — banner is live for 24h." : "Saved as draft (viewers won't see it).");
    } catch (e) {
      setFlash(e.response?.data?.detail || "Save failed — check your input.");
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear the tribute? Viewers will no longer see it.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/site/tribute/clear`, {}, { headers: authHeaders() });
      setTribute(null);
      setDraft(emptyDraft);
      setGallery([]);
      // CLEAR auto-archives the current — refetch so the list stays fresh.
      await refetchArchives();
      window.dispatchEvent(new CustomEvent("crt:tribute-updated", { detail: null }));
      flashOK("Cleared.");
    } catch (e) {
      setFlash("Clear failed.");
    } finally {
      setBusy(false);
    }
  };

  // Save the current draft to the archive WITHOUT publishing. Lets
  // the owner prep a tribute in advance and hold it until they're
  // ready to send viewers the "▲ IN MEMORIAM" banner. Uses the new
  // POST /site/tribute/archive endpoint which mirrors the standard
  // publish path except it never touches the live tribute doc.
  const handleSaveDraft = async () => {
    if (!draft.name.trim()) {
      setFlash("Add a name before saving as a draft.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: draft.name.trim().slice(0, CAP_NAME),
        birth_year: parseYear(draft.birth_year),
        death_year: parseYear(draft.death_year),
        obituary: draft.obituary.slice(0, CAP_OBIT),
        hero_url: draft.hero_url.trim().slice(0, CAP_URL) || null,
        linked_channel_id: draft.linked_channel_id || null,
        linked_video_id: draft.linked_video_id || null,
      };
      const r = await axios.post(`${API}/site/tribute/archive`, payload, {
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });
      if (r.data && Array.isArray(r.data.items)) setArchives(r.data.items);
      setArchivesOpen(true);
      flashOK("Saved as draft — LOAD it from the archive when ready to publish.");
    } catch (e) {
      setFlash(e.response?.data?.detail || "Draft save failed.");
    } finally {
      setBusy(false);
    }
  };

  // Load an archived tribute (draft OR past published) back into the
  // editor for further editing. Pulls the full doc via
  // GET /site/tribute/archive/{id} so we get the obituary and any
  // linked channel/video that the light metadata list didn't include.
  // NOTE: does NOT auto-publish — the owner still has to click
  // PUBLISH after any edits. Gallery + audio aren't re-attached
  // because those are managed on the live tribute doc separately
  // (see the endpoint's scope note).
  const handleLoadArchive = async (archiveId) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/site/tribute/archive/${archiveId}`);
      const doc = r.data || {};
      setDraft({
        enabled: false, // Drafts default to disabled — owner opts in when publishing.
        name: doc.name || "",
        birth_year: doc.birth_year != null ? String(doc.birth_year) : "",
        death_year: doc.death_year != null ? String(doc.death_year) : "",
        obituary: doc.obituary || "",
        hero_url: doc.hero_url || "",
        linked_channel_id: doc.linked_channel_id || "",
        linked_video_id: doc.linked_video_id || "",
      });
      flashOK("Loaded into editor. Edit + PUBLISH when ready.");
    } catch (e) {
      setFlash("Load failed — that archive may have been deleted.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddGallery = async () => {
    const url = galleryDraft.url.trim();
    if (!url) { setFlash("Paste an image URL first."); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/site/tribute/gallery`,
        { url: url.slice(0, CAP_URL), caption: galleryDraft.caption.slice(0, CAP_CAPTION) },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      setGallery(r.data.gallery || []);
      setGalleryDraft({ url: "", caption: "" });
      flashOK("Photo added.");
    } catch (e) {
      setFlash(e.response?.data?.detail || "Couldn't add photo.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteGallery = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/site/tribute/gallery/${id}`, { headers: authHeaders() });
      setGallery(r.data.gallery || []);
    } catch (e) {
      setFlash("Couldn't remove.");
    } finally {
      setBusy(false);
    }
  };

  if (!isOwner) return null;

  const realChannels = (channels || []).filter((c) => !c.virtual && !c.isSubChannel);
  const linkedChannel = realChannels.find((c) => c.id === draft.linked_channel_id);

  return (
    <div
      className="absolute top-12 right-0 w-[520px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in_srgb,var(--phosphor)_35%,transparent)] z-30"
      data-testid="tribute-panel"
      style={{ fontFamily: "VT323, monospace", fontSize: "15px", lineHeight: 1.35 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm tracking-[0.3em] flex items-center gap-2">
          <Heart size={14} className="opacity-80" />
          ┌─ TRIBUTE / IN MEMORIAM ─┐
        </div>
        <button onClick={onClose} className="text-[var(--phosphor)]" data-testid="tribute-panel-close">
          <X size={16} />
        </button>
      </div>

      <div className="text-[11px] opacity-65 leading-snug mb-3">
        One evolving tribute — replace when a new figure needs remembering. All fields are optional except the name.
        Publishing shows a subtle &ldquo;▲ NEW TRIBUTE&rdquo; banner to every viewer for 24 hours.
      </div>

      {/* Enabled toggle */}
      <label className="flex items-start gap-2 cursor-pointer select-none mb-3 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
          data-testid="tribute-enabled"
          className="accent-[var(--phosphor)] mt-1"
        />
        <div className="flex-1">
          <div className="text-[12px] tracking-[0.22em]">PUBLISH TO VIEWERS</div>
          <div className="text-[11px] opacity-60 leading-snug">
            Off = save privately as a draft. On = every viewer sees the ▲ NEW TRIBUTE banner for 24h.
          </div>
        </div>
      </label>

      {/* Name */}
      <Field label="FULL NAME" hint="e.g. René Auberjonois">
        <input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, CAP_NAME) }))}
          maxLength={CAP_NAME}
          placeholder="Name of the person being honoured"
          data-testid="tribute-name"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[13px] outline-none tracking-[0.08em]"
        />
      </Field>

      {/* Birth / death years */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Field label="BIRTH YEAR" hint="optional">
          <input
            type="number"
            min={0}
            max={9999}
            value={draft.birth_year}
            onChange={(e) => setDraft((d) => ({ ...d, birth_year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))}
            placeholder="1940"
            data-testid="tribute-birth-year"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[13px] outline-none tabular-nums"
          />
        </Field>
        <Field label="DEATH YEAR" hint="optional">
          <input
            type="number"
            min={0}
            max={9999}
            value={draft.death_year}
            onChange={(e) => setDraft((d) => ({ ...d, death_year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))}
            placeholder="2024"
            data-testid="tribute-death-year"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[13px] outline-none tabular-nums"
          />
        </Field>
      </div>

      {/* Hero image URL */}
      <Field label="HERO IMAGE URL" hint="Portrait / landscape shot — appears at the top of the tribute page">
        <input
          value={draft.hero_url}
          onChange={(e) => setDraft((d) => ({ ...d, hero_url: e.target.value.slice(0, CAP_URL) }))}
          maxLength={CAP_URL}
          placeholder="https://…image.jpg"
          data-testid="tribute-hero-url"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[13px] outline-none tracking-[0.05em]"
        />
      </Field>

      {/* Obituary text */}
      <Field label="OBITUARY / TEXT" hint={`Free-form remembrance · ${draft.obituary.length}/${CAP_OBIT}`}>
        <textarea
          value={draft.obituary}
          onChange={(e) => setDraft((d) => ({ ...d, obituary: e.target.value.slice(0, CAP_OBIT) }))}
          maxLength={CAP_OBIT}
          rows={6}
          placeholder={"Best remembered for their role as…\n\nAdd biographical detail, quotes, or a personal note."}
          data-testid="tribute-obituary"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2 text-[13px] outline-none resize-y leading-snug"
          style={{ fontFamily: "VT323, monospace" }}
        />
      </Field>

      {/* Audio upload — direct .mp3 (or .ogg/.wav/.m4a) upload, streamed
          back through /api/site/tribute/audio. Replaces the earlier
          "paste an audio URL" flow so the operator can attach a clip
          without hosting it somewhere first. If the currently-published
          tribute still has a legacy `audio_url` (from before this
          switch), we surface it as read-only so the operator knows to
          upload a replacement. */}
      <Field
        label="AUDIO CLIP (MP3)"
        hint={
          tribute?.audio_path
            ? `Uploaded: ${tribute.audio_filename || "tribute audio"} · plays softly on tribute open`
            : "Direct .mp3 / .ogg / .wav / .m4a upload · max 8 MB · plays softly on tribute open"
        }
      >
        <div className="flex items-center gap-1">
          <input
            type="file"
            accept="audio/mpeg,audio/ogg,audio/wav,audio/mp4,audio/x-m4a,audio/aac,audio/*"
            data-testid="tribute-audio-file"
            className="hidden"
            ref={(el) => { audioFileInputRef.current = el; }}
            onChange={async (e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = "";
              if (!f) return;
              if (f.size > 8_000_000) {
                setFlash("Audio too large — 8 MB max.");
                return;
              }
              setBusy(true);
              try {
                const form = new FormData();
                form.append("file", f);
                const r = await axios.post(`${API}/site/tribute/audio`, form, {
                  headers: { ...authHeaders() },
                });
                setTribute(r.data);
                flashOK(`Uploaded ${f.name} (${Math.round(f.size / 1024)} KB).`);
              } catch (err) {
                setFlash(err.response?.data?.detail || "Upload failed.");
              } finally {
                setBusy(false);
              }
            }}
          />
          <button
            type="button"
            onClick={() => audioFileInputRef.current && audioFileInputRef.current.click()}
            disabled={busy}
            data-testid="tribute-audio-upload"
            className="flex-1 border border-[var(--phosphor)] py-1.5 text-[11px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {tribute?.audio_path ? "REPLACE AUDIO" : "UPLOAD AUDIO"}
          </button>
          {tribute?.audio_path && (
            <>
              <a
                href={`${API}/site/tribute/audio?v=${encodeURIComponent(tribute.updated_at || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tribute-audio-preview"
                className="px-2 py-1.5 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] text-[11px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors flex items-center gap-1"
                title="Open uploaded audio in a new tab to preview"
              >
                ▶ TEST
              </a>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Remove uploaded audio?")) return;
                  setBusy(true);
                  try {
                    const r = await axios.delete(`${API}/site/tribute/audio`, { headers: authHeaders() });
                    setTribute(r.data);
                    flashOK("Audio cleared.");
                  } catch (err) {
                    setFlash("Clear failed.");
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                data-testid="tribute-audio-clear"
                className="px-2 py-1.5 border border-[#FF3B30]/60 text-[#FF3B30] text-[11px] tracking-widest hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40"
                title="Remove the uploaded audio clip"
              >
                <Trash2 size={11} />
              </button>
            </>
          )}
        </div>
        {tribute?.audio_url && !tribute?.audio_path && (
          <div className="mt-1 text-[10px] opacity-60 leading-snug truncate" data-testid="tribute-audio-legacy-url">
            Legacy URL still attached · <span className="underline decoration-dotted">{tribute.audio_url}</span> — upload a file to replace it.
          </div>
        )}
      </Field>

      {/* Linked channel + optional tape */}
      <Field label="LINK TO A CHANNEL (OPTIONAL)" hint="Shown on the tribute as ‘Watch their work on CH …’">
        <select
          value={draft.linked_channel_id}
          onChange={(e) => setDraft((d) => ({ ...d, linked_channel_id: e.target.value, linked_video_id: "" }))}
          data-testid="tribute-linked-channel"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[12px] outline-none tracking-[0.08em]"
        >
          <option value="">— no linked channel —</option>
          {realChannels
            .slice()
            .sort((a, b) => (a.number || 0) - (b.number || 0))
            .map((c) => (
              <option key={c.id} value={c.id}>
                CH {String(c.number).padStart(2, "0")} — {c.name}
              </option>
            ))}
        </select>
      </Field>

      {linkedChannel && (
        <Field label="OPTIONAL — LINK A SPECIFIC TAPE ID" hint="Leave blank to link the whole channel">
          <input
            value={draft.linked_video_id}
            onChange={(e) => setDraft((d) => ({ ...d, linked_video_id: e.target.value.trim() }))}
            placeholder="tape uuid (blank = whole channel)"
            data-testid="tribute-linked-video"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[12px] outline-none tracking-[0.05em]"
          />
        </Field>
      )}

      {/* Gallery */}
      <div className="mb-3 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2">
        <div className="text-[12px] tracking-[0.22em] opacity-85 mb-1">
          GALLERY <span className="opacity-60">({gallery.length})</span>
        </div>
        <div className="text-[11px] opacity-60 leading-snug mb-2">
          Extra photos shown as a strip beneath the obituary. Add one at a time.
        </div>
        <div className="space-y-1 mb-2">
          {gallery.map((g) => (
            <div
              key={g.id}
              className="flex items-center gap-2 border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] p-1.5"
              data-testid={`tribute-gallery-item-${g.id}`}
            >
              <img
                src={g.url}
                alt=""
                className="w-12 h-12 object-cover border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] bg-black shrink-0"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] tracking-[0.05em] truncate">{g.caption || "(no caption)"}</div>
                <div className="text-[10px] opacity-55 truncate">{g.url}</div>
              </div>
              <button
                type="button"
                onClick={() => handleDeleteGallery(g.id)}
                disabled={busy}
                data-testid={`tribute-gallery-delete-${g.id}`}
                className="text-[#FF3B30] border border-[#FF3B30]/50 px-1.5 py-0.5 text-[10px] tracking-widest hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          {gallery.length === 0 && (
            <div className="text-[11px] opacity-45 italic">No photos yet.</div>
          )}
        </div>
        <div className="grid grid-cols-1 gap-1">
          <input
            value={galleryDraft.url}
            onChange={(e) => setGalleryDraft((g) => ({ ...g, url: e.target.value.slice(0, CAP_URL) }))}
            placeholder="https://…photo.jpg"
            data-testid="tribute-gallery-url"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[12px] outline-none tracking-[0.05em]"
          />
          <div className="flex gap-1">
            <input
              value={galleryDraft.caption}
              onChange={(e) => setGalleryDraft((g) => ({ ...g, caption: e.target.value.slice(0, CAP_CAPTION) }))}
              placeholder="Caption (optional)"
              maxLength={CAP_CAPTION}
              data-testid="tribute-gallery-caption"
              className="flex-1 min-w-0 bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-1.5 text-[12px] outline-none"
            />
            <button
              type="button"
              onClick={handleAddGallery}
              disabled={busy || !galleryDraft.url.trim()}
              data-testid="tribute-gallery-add"
              className="border border-[var(--phosphor)] px-2 py-1 text-[11px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus size={11} /> ADD
            </button>
          </div>
        </div>
      </div>

      {/* Archive — auto-populated on every PUBLISH-over and CLEAR.
          Owner can prune entries here (e.g. remove a test tribute
          they published while trialling the feature). */}
      <div className="mb-3 border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2">
        <button
          type="button"
          onClick={() => setArchivesOpen((v) => !v)}
          data-testid="tribute-archive-toggle"
          className="w-full flex items-center justify-between text-left"
        >
          <span className="text-[12px] tracking-[0.22em] opacity-85">
            ARCHIVE <span className="opacity-60">({archives.length})</span>
          </span>
          <span className="text-[11px] opacity-60">{archivesOpen ? "▲ HIDE" : "▼ SHOW"}</span>
        </button>
        {archivesOpen && (
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1" data-testid="tribute-archive-list">
            {archives.length === 0 && (
              <div className="text-[11px] opacity-45 italic">No past tributes archived yet.</div>
            )}
            {archives.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-2 border border-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] p-1.5"
                data-testid={`tribute-archive-row-${a.id}`}
              >
                {a.hero_url ? (
                  <img
                    src={a.hero_url}
                    alt=""
                    className="w-10 h-10 object-cover border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] bg-black shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] shrink-0 opacity-40">
                    <Heart size={12} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] tracking-[0.05em] truncate flex items-center gap-1.5">
                    {a.name}
                    {a.is_draft && (
                      <span
                        className="text-[8px] tracking-[0.25em] px-1 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] opacity-80"
                        title="Saved as a draft — not yet published to viewers"
                      >
                        DRAFT
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] opacity-55 tabular-nums">
                    {a.birth_year || "?"} — {a.death_year || "?"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleLoadArchive(a.id)}
                  disabled={busy}
                  data-testid={`tribute-archive-load-${a.id}`}
                  title={a.is_draft
                    ? "Load this draft into the editor to finish + publish"
                    : "Re-open this past tribute in the editor (edits + republish create a new archive entry)"
                  }
                  className="text-[color-mix(in_srgb,var(--phosphor)_90%,transparent)] border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-1.5 py-0.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
                >
                  LOAD
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Delete archived tribute for ${a.name}? This cannot be undone.`)) return;
                    setBusy(true);
                    try {
                      await axios.delete(`${API}/site/tribute/archive/${a.id}`, { headers: authHeaders() });
                      await refetchArchives();
                    } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  data-testid={`tribute-archive-delete-${a.id}`}
                  className="text-[#FF3B30] border border-[#FF3B30]/50 px-1.5 py-0.5 text-[10px] tracking-widest hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation strip */}
      {flash && (
        <div
          className="text-[11px] opacity-90 mb-2 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] px-2 py-1"
          data-testid="tribute-flash"
        >
          &gt; {flash}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleClear}
          disabled={busy || !tribute}
          data-testid="tribute-clear"
          className="flex-1 border border-[#FF3B30] text-[#FF3B30] py-1.5 text-[12px] tracking-widest hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          CLEAR
        </button>
        <button
          onClick={handleSaveDraft}
          disabled={busy || !draft.name.trim()}
          data-testid="tribute-archive-draft"
          title="Stash the current form as a draft in the ARCHIVE for later. Doesn't touch the live tribute."
          className="flex-1 border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] text-[color-mix(in_srgb,var(--phosphor)_85%,transparent)] py-1.5 text-[12px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ARCHIVE
        </button>
        <button
          onClick={handleSave}
          disabled={busy}
          data-testid="tribute-save"
          className="flex-1 border border-[var(--phosphor)] py-1.5 text-[12px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {draft.enabled ? "PUBLISH" : "SAVE"}
        </button>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <div className="text-[12px] tracking-[0.22em] opacity-85 mb-0.5">{label}</div>
      {children}
      {hint && <div className="text-[10px] opacity-55 mt-0.5 leading-snug">{hint}</div>}
    </div>
  );
}

function parseYear(s) {
  const n = parseInt((s || "").toString().trim(), 10);
  if (Number.isNaN(n) || n < 0 || n > 9999) return null;
  return n;
}
